import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/ui/Mascot";
import type { MascotName } from "@/components/ui/mascots";
import { cn } from "@/lib/utils/cn";

export interface EmptyStateProps {
  icon?: ReactNode;
  /**
   * W41.0 E5: the mascot that replaces the icon slot on an empty screen.
   *
   * W44 moved the default from `lilac` to `coral`. The block used to be lilac with
   * a **white** character on it; the block is white now, and coral is the only
   * mascot that clears the 3:1 a graphical object needs on white (3.58:1, vs
   * lilac 2.69 · aqua 2.14 · lime 1.19). F17 was never 「角色要与卡片不同色」 — it is
   * 「角色要看得见」, and a white character on a white card is exactly the failure
   * it was written to prevent. It is still a named prop rather than something a
   * caller assembles from `icon`, for the same reason: the icon branch keeps its
   * own halo behaviour and no existing call site changes.
   */
  mascot?: MascotName;
  title: string;
  description?: string;
  /**
   * Convenience shortcut for the common "single primary button" CTA pattern.
   * Composed internally as `<Button variant="primary">{actionLabel}</Button>`.
   *
   * If both `actionLabel` + `onAction` AND `cta` are provided, `cta` wins
   * (full ReactNode flexibility preserved for advanced cases).
   *
   * Added per `wiki/w2.3-non-spec-audit.md` L2 — was previously requiring
   * consumers to wrap a Button manually.
   */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Advanced CTA slot — accept any ReactNode (e.g. multiple buttons, a
   * Link wrapping a Button, a custom <a>).  Use `actionLabel` + `onAction`
   * for the common single-primary-button case.
   */
  cta?: ReactNode;
  /** Brand-tint halo background (you / partner / shared / gold). Defaults to `you`. */
  halo?: "you" | "partner" | "shared" | "gold";
  className?: string;
}

/**
 * P4 — EmptyState.
 *
 * Per `component-api.md §2 P4`:
 *   - centered icon (halo background) + title + description + CTA
 *   - 圆环 icon 用 brand-tint halo background
 *   - Used when list is empty — gives user a clear next step.
 */
export function EmptyState({
  icon,
  mascot = "coral",
  title,
  description,
  actionLabel,
  onAction,
  cta,
  halo = "you",
  className,
}: EmptyStateProps) {
  // Compose actionLabel/onAction → cta when cta not explicitly provided.
  const resolvedCta: ReactNode =
    cta ??
    (actionLabel && onAction ? (
      <Button variant="primary" size="md" onClick={onAction}>
        {actionLabel}
      </Button>
    ) : null);

  return (
    <div
      data-component="EmptyState"
      data-halo={halo}
      data-has-action={actionLabel || cta ? "true" : undefined}
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-10 text-center",
        // W44: white, not `surface-lilac`. A saturated block on 「還沒有 X」 is an
        // **accent with nothing to accent** — W43 (`ADR-0109`) gave `accent` a
        // meaning (≤3 per screen, earned by content) and gave white the default,
        // and an empty state is not content. It was also a rule violation, not
        // only a taste one: `EmptyState` is used on data screens, and F6 says
        // data screens carry **no** saturated card, so a lilac empty state put one
        // there on `/albums`, `/calendar` and the trash lists.
        //
        // The mascot stays. It is the thing that makes an empty screen feel
        // addressed rather than broken, and F17's rule is about the mascot not
        // disappearing — which is a contrast question against *this* card now,
        // and coral on white is 3.58:1.
        "surface-plain rounded-lg",
        "shadow-card",
        className,
      )}
    >
      {/* W41.0 E5: the mascot takes the icon slot unless a caller passes a real
          icon. W44 dropped the forced white fill — see the note at the call site
          for why that override would now be invisible. The `icon` branch keeps the
          existing halo behaviour intact, so no existing call site changes. */}
      {icon ? (
        <div
          aria-hidden
          className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center",
            halo === "you" && "bg-you-tint text-you-strong",
            halo === "partner" && "bg-partner-tint text-partner-strong",
            halo === "shared" && "bg-shared-tint text-shared-strong",
            halo === "gold" && "bg-gold-tint text-gold-ink",
          )}
        >
          <span className="icon-lg">{icon}</span>
        </div>
      ) : (
        // W44: **own colour, not white.** This block was lilac and the mascot was
        // drawn white on top of it (F17: a character never shares its card's
        // colour). The card is white now, so `fill="#FFFFFF"` would have made the
        // mascot *literally invisible* — the same class of defect as the
        // topbar-avatar collision, reached from the opposite direction.
        //
        // And the default is `coral` because it is the only mascot that reaches
        // the 3:1 a graphical object needs on this ground: coral 3.58:1 on white,
        // lilac 2.69, aqua 2.14, lime 1.19. A caller passing a lighter mascot gets
        // the same washed-out character, which is why the default moved with the
        // measurement rather than with taste.
        <Mascot name={mascot} size={112} />
      )}
      <h2 className="text-h3 text-ink-1">{title}</h2>
      {description ? <p className="text-body-sm text-ink-2 max-w-[36ch]">{description}</p> : null}
      {resolvedCta ? (
        <div className="mt-2" data-testid="empty-state-cta">
          {resolvedCta}
        </div>
      ) : null}
    </div>
  );
}
